import { v4 as uuidv4 } from "uuid";
import { z } from "zod";

export const ChecklistOptionSchema = z.object({
  id: z
    .string()
    .uuid()
    .default(() => uuidv4()),
  optionText: z
    .string()
    .min(1, "Option text is required")
    .refine((val) => val.trim().length > 0),
  displayOrder: z.number().min(0, "Display order must be non-negative")
});

export const ChecklistQuestionSchema = z
  .object({
    id: z
      .string()
      .uuid()
      .default(() => uuidv4()),
    checklistQuestionTypeId: z.string().uuid("Invalid question type ID format"),
    questionText: z
      .string()
      .min(1, "Question text is required")
      .refine((val) => val.trim().length > 0),
    displayOrder: z.number().min(0, "Display order must be non-negative"),
    isRequired: z.boolean().default(false),
    options: z.array(ChecklistOptionSchema).default([]),
    questionType: z
      .object({
        code: z.string()
      })
      .optional(),
    questionTypeCode: z.string().optional()
  })
  .superRefine((question, ctx) => {
    const hasOptions = question.options.length > 0;

    if (hasOptions) {
      const optionTextToIndexes = new Map<string, number[]>();
      question.options.forEach((option, idx) => {
        const key = option.optionText.trim().toLowerCase();
        const arr = optionTextToIndexes.get(key) ?? [];
        arr.push(idx);
        optionTextToIndexes.set(key, arr);
      });

      optionTextToIndexes.forEach((indexes) => {
        if (indexes.length > 1) {
          indexes.forEach((idx: number) => {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Option text is already taken",
              path: ["options", idx, "optionText"]
            });
          });
        }
      });
    }
  });

export const ChecklistSectionSchema = z
  .object({
    id: z
      .string()
      .uuid()
      .default(() => uuidv4()),
    title: z
      .string()
      .min(1, "Section title is required")
      .refine((val) => val.trim().length > 0),
    description: z.string().optional().nullable(),
    displayOrder: z.number().min(0, "Display order must be non-negative"),
    questions: z.array(ChecklistQuestionSchema).min(1, "At least one question is required")
  })
  .superRefine((section, ctx) => {
    const questionTextToIndexes = new Map<string, number[]>();
    section.questions.forEach((q, idx) => {
      const key = q.questionText.trim().toLowerCase();
      const arr = questionTextToIndexes.get(key) ?? [];
      arr.push(idx);
      questionTextToIndexes.set(key, arr);
    });

    questionTextToIndexes.forEach((indexes) => {
      if (indexes.length > 1) {
        indexes.forEach((idx: number) => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Question text is already taken",
            path: ["questions", idx, "questionText"]
          });
        });
      }
    });
  });

export const ChecklistSchema = z
  .object({
    checklist: z.object({
      name: z
        .string()
        .min(1, "Checklist name is required")
        .refine((val) => val.trim().length > 0),
      description: z.string().optional().nullable(),
      sections: z.array(ChecklistSectionSchema).min(1, "At least one section is required")
    })
  })
  .superRefine((data, ctx) => {
    const sectionTitleToIndexes = new Map<string, number[]>();
    data.checklist.sections.forEach((section, idx) => {
      const key = section.title.trim().toLowerCase();
      const arr = sectionTitleToIndexes.get(key) ?? [];
      arr.push(idx);
      sectionTitleToIndexes.set(key, arr);
    });

    sectionTitleToIndexes.forEach((indexes) => {
      if (indexes.length > 1) {
        indexes.forEach((idx: number) => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Section title is already taken",
            path: ["checklist", "sections", idx, "title"]
          });
        });
      }
    });
  });

export type ChecklistOptionType = z.infer<typeof ChecklistOptionSchema>;
export type ChecklistQuestionType = z.infer<typeof ChecklistQuestionSchema>;
export type ChecklistSectionType = z.infer<typeof ChecklistSectionSchema>;
export type ChecklistType = z.infer<typeof ChecklistSchema>;
